import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSun,
  faMoon,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "../../contexts/ThemeContext";
import styles from "./ToolMenu.module.css";

const LegendIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M5 7h14M5 12h10M5 17h6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle cx="18" cy="12" r="1.4" fill="currentColor" />
    <circle cx="14" cy="17" r="1.4" fill="currentColor" />
  </svg>
);

const ResultsIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M5 5h14v14H5z"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path
      d="M8 13l3 3 5-7"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const NewIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 5v14M5 12h14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const ChevronIcon = ({ expanded }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={styles.chevron}
    style={{ transform: expanded ? "rotate(90deg)" : "rotate(0deg)" }}
  >
    <path
      d="M9 18l6-6-6-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SettingsIcon = () => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

function ToolMenu({
  title = "Tools",
  categories = [],
  onLegend,
  onResults,
  onNewAnalysis,
  resultsCount,
  onCollapseChange,
}) {
  const { toggleTheme, isLight, canToggleTheme } = useTheme();
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showSettings, setShowSettings] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleToggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    onCollapseChange?.(newState);
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const renderAction = (action) => {
    if (action.type === "slider") {
      return (
        <div key={action.id || action.label} className={styles.sliderRow}>
          <div className={styles.sliderHeader}>
            {action.icon && (
              <span className={styles.actionIcon}>{action.icon}</span>
            )}
            <div className={styles.actionText}>
              <span className={styles.actionLabel}>{action.label}</span>
            </div>
            <span className={styles.sliderValue}>
              {action.displayValue
                ? action.displayValue(action.value)
                : `${action.value}${action.unit || ""}`}
            </span>
          </div>
          <input
            type="range"
            min={action.min}
            max={action.max}
            step={action.step || 1}
            value={action.value}
            onChange={(e) => action.onChange?.(Number(e.target.value))}
            className={styles.slider}
          />
        </div>
      );
    }

    return (
      <button
        key={action.id || action.label}
        className={`${
          action.variant === "solid" ? styles.primaryButton : styles.linkButton
        } ${action.isActive ? styles.active : ""}`}
        type="button"
        onClick={action.onClick}
      >
        <div className={styles.buttonContent}>
          {action.icon && (
            <span className={styles.actionIcon}>{action.icon}</span>
          )}
          <span className={styles.actionText}>
            <span className={styles.actionLabel}>{action.label}</span>
            {action.meta && (
              <span className={styles.actionMeta}>{action.meta}</span>
            )}
          </span>
        </div>
      </button>
    );
  };

  return (
    <aside className={`${styles.menu} ${isCollapsed ? styles.collapsed : ""}`}>
      <div className={styles.header}>
        <button
          className={styles.collapseButton}
          onClick={handleToggleCollapse}
          type="button"
          title={isCollapsed ? "Expand menu" : "Collapse menu"}
          aria-label={isCollapsed ? "Expand menu" : "Collapse menu"}
        >
          <FontAwesomeIcon
            icon={isCollapsed ? faChevronLeft : faChevronRight}
          />
        </button>
        {!isCollapsed && <span className={styles.headerTitle}>{title}</span>}
      </div>

      {!isCollapsed && (
        <>
          {onNewAnalysis && (
            <div className={styles.newAnalysisSection}>
              <button
                className={styles.primaryButton}
                type="button"
                onClick={onNewAnalysis}
              >
                <div className={styles.buttonContent}>
                  <NewIcon />
                  <span>New Analysis</span>
                </div>
              </button>
            </div>
          )}

          <div className={styles.section}>
            <div className={styles.sectionLabel}>Navigation</div>
            <div className={styles.topActions}>
              <button
                className={styles.linkButton}
                type="button"
                onClick={onLegend}
              >
                <div className={styles.buttonContent}>
                  <LegendIcon />
                  <span>Legend</span>
                </div>
              </button>
              <button
                className={styles.linkButton}
                type="button"
                onClick={onResults}
              >
                <div className={styles.buttonContent}>
                  <ResultsIcon />
                  <span>Results</span>
                </div>
                {resultsCount !== undefined && (
                  <span className={styles.badge}>{resultsCount}</span>
                )}
              </button>
            </div>
          </div>

          <hr className={styles.rule} />

          <div className={styles.section}>
            <div className={styles.sectionLabel}>Tools</div>
            <div className={styles.actions}>
              {categories.length === 0 ? (
                <div className={styles.empty}>No tools</div>
              ) : (
                categories.map((category) => {
                  const isExpanded = expandedCategories[category.id];
                  return (
                    <div key={category.id} className={styles.category}>
                      <button
                        className={styles.categoryHeader}
                        type="button"
                        onClick={() => toggleCategory(category.id)}
                      >
                        <div className={styles.buttonContent}>
                          {category.icon && (
                            <span className={styles.actionIcon}>
                              {category.icon}
                            </span>
                          )}
                          <span className={styles.actionText}>
                            <span className={styles.actionLabel}>
                              {category.label}
                            </span>
                          </span>
                        </div>
                        <ChevronIcon expanded={isExpanded} />
                      </button>
                      {isExpanded &&
                        category.items &&
                        category.items.length > 0 && (
                          <div className={styles.submenu}>
                            {category.items.map((action) =>
                              renderAction(action)
                            )}
                          </div>
                        )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className={styles.settingsSection}>
            <button
              className={styles.settingsButton}
              type="button"
              onClick={() => setShowSettings(!showSettings)}
              title="Settings"
            >
              <div className={styles.buttonContent}>
                <SettingsIcon />
                <span>Settings</span>
              </div>
              <ChevronIcon expanded={showSettings} />
            </button>

            {showSettings && canToggleTheme && (
              <div className={styles.settingsPanel}>
                <div className={styles.settingsGroup}>
                  <div className={styles.settingsLabel}>Theme</div>
                  <div className={styles.themeToggleGroup}>
                    <button
                      className={`${styles.themeToggleButton} ${
                        isLight ? styles.active : ""
                      }`}
                      onClick={toggleTheme}
                      type="button"
                    >
                      <FontAwesomeIcon icon={faSun} />
                      <span>Light</span>
                    </button>
                    <button
                      className={`${styles.themeToggleButton} ${
                        !isLight ? styles.active : ""
                      }`}
                      onClick={toggleTheme}
                      type="button"
                    >
                      <FontAwesomeIcon icon={faMoon} />
                      <span>Dark</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </aside>
  );
}

export default ToolMenu;
